// noinspection DuplicatedCode

import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { keyframes, styled } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { skipToken } from "@reduxjs/toolkit/query";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Loader from "_shared/_views/loading/loader";
import AHBoolean from "_shared/booleans/boolean";
import EntityMember from "_shared/entity-members/entity-member";
import Guid from "_shared/guids/guid";
import _ from "lodash";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import { getIndividualsByUserIds } from "marketplace/individuals/_stores/individuals-redux-slice";
import MarketplaceTeam from "marketplace/teams/team";
import { enqueueSnackbar } from "notistack";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useSession } from "users/__session/session-context";
import { AccountType } from "users/account-types/account-type";
import { Masked } from "work/_shared/masked-fields";
import ProposalBuilder from "work/proposals/__builder/proposal-builder";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import ProposalIssues, { ProposalIssueLevel } from "work/proposals/issues/proposal-issues";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import { TeamTemplateOrderField } from "work/team-templates/_api/queries/team-template-order-field";
import TeamTemplateQuery, {
  TeamTemplateFilterParameters,
  TeamTemplateOrderParameters,
} from "work/team-templates/_api/queries/team-template-query";
import {
  getErrorLoadingTeamTemplatesByQuery,
  getIsLoadingTeamTemplatesByQuery,
  getTeamTemplatesByQuery,
  getTeamTemplatesQueryByInterface,
  populateTeamTemplatesByQuery,
} from "work/team-templates/_store/team-templates-redux-slice";
import TeamTemplate from "work/team-templates/team-template";
import EditVendorTeam from "work/work-agreements/teams/vendor-team/_views/vendor-team-edit-view";
import VendorTeam from "work/work-agreements/teams/vendor-team/vendor-team";

const pulseBorder = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 1px rgba(21, 101, 192, 0.1);
    }
    50% {
    border-color: rgb(21, 101, 192);
    box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.33);
  }
`;
const ListContainer = styled(Grid)(() => ({
  height: "100%",
  minHeight: "20rem",
}));
const TemplatePanel = styled('div', {
  shouldForwardProp: (prop) => prop !== "open"
})<{ open?: boolean }>(({ theme, open }) => ({
  transition: theme.transitions.create(["margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  width: open ? 0 : 'initial',
}));
const TemplateContainer = styled(Grid)(({ theme }) => ({
  [theme.breakpoints.down("lg")]: {
    width: "100%",
  },
}));
const TemplateList = styled(List)(() => ({
  maxHeight: "25rem",
  minWidth: "20rem",
  overflowY: "auto",
}));
const TemplateLoader = styled(CircularProgress)(({ theme }) => ({
  color: theme.palette.primary.main,
  margin: theme.spacing(2),
}));
const TeamSelect = styled(EditVendorTeam)(({ theme }) => ({
  [theme.breakpoints.down("lg")]: {
    marginTop: theme.spacing(2),
  },
  marginLeft: theme.spacing(2),
}));
const HeadControls = styled("div")(({ theme }) => ({
  alignItems: "baseline",
  display: "flex",
  justifyContent: "space-between",
  marginTop: theme.spacing(1),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
}));
const IssueContainer = styled("div", { shouldForwardProp: (prop) => prop !== "active" })<{ active: boolean }>(
  ({ theme, active }) => ({
    animation: active ? `${pulseBorder} 1.5s ease-in-out infinite` : "none",
    margin: active ? theme.spacing(1) : "initial",
    marginBottom: theme.spacing(3),
    marginLeft: theme.spacing(active ? 0.5 : 0),
    marginTop: active ? 0 : theme.spacing(1),
    padding: active ? theme.spacing(1) : "initial",
    borderRadius: theme.shape.borderRadius,
  }),
);

type TeamTabProps = {
  activeReviewField?: ProposalField;
  proposalBuilder: ProposalBuilder;
  issues?: ProposalIssues;
  disableEditing?: boolean;
  isSaved: boolean;
  isDrawerOpen: boolean;
  onProposalBuilderUpdated: (newProposalBuilder: ProposalBuilder) => void;
  commentThreads: CommentThread[];
  commentsToolTip: string;
  onCommentsClicked?: (field: ProposalField, name?: string) => void;
};

export default function TeamTab(props: Readonly<TeamTabProps>) {
  const INTERFACE_NAME = "DraftProposalTeamTab";
  const {
    activeReviewField,
    proposalBuilder,
    disableEditing,
    isSaved,
    isDrawerOpen,
    onProposalBuilderUpdated,
    commentThreads,
    commentsToolTip,
    onCommentsClicked,
  } = props;

  const session = useSession();
  const dispatch = useAttorneyHubDispatch();
  const filterParameters = new TeamTemplateFilterParameters({ creatorEntityId: session.user!.legalEntityId });
  const orderParameters = new TeamTemplateOrderParameters({
    orderBy: TeamTemplateOrderField.Name,
    orderDir: OrderDirection.Descending,
  });
  const defaultTemplateQuery: TeamTemplateQuery = new TeamTemplateQuery({
    filterParameters,
    orderParameters,
    paginationParameters: PaginationParameters.DEFAULT,
  });

  const templateQuery = useSelector(getTeamTemplatesQueryByInterface(INTERFACE_NAME)) ?? defaultTemplateQuery;
  const teamTemplates = useSelector(getTeamTemplatesByQuery(templateQuery));
  const isLoadingTeamTemplates = useSelector(getIsLoadingTeamTemplatesByQuery(templateQuery));
  const errorLoadingTeamTemplates = useSelector(getErrorLoadingTeamTemplatesByQuery(templateQuery));
  const proposal = proposalBuilder.buildNewDraft();
  const individualUserIds: Set<string> = useMemo(() => {
    const userIds: Set<string> = new Set();
    if (teamTemplates === undefined) return userIds;
    if (proposal) proposal.associatedUserIds.forEach((id) => userIds.add(id.value));
    for (const templateTeam of teamTemplates?.data ?? []) {
      userIds.add(templateTeam.leader.userId.value);
      for (const member of templateTeam.members) {
        userIds.add(member.userId.value);
      }
    }
    return userIds;
  }, [proposal, teamTemplates]);

  const individuals = useSelector(getIndividualsByUserIds(individualUserIds));
  const missingIndividualUserIds = useMemo(() => {
    const loadedUserIds = new Set(individuals.map((ind) => ind.userId.value));
    const missingIds = new Set<string>();
    individualUserIds.forEach((userId) => {
      if (!loadedUserIds.has(userId)) {
        missingIds.add(userId);
      }
    });
    return missingIds;
  }, [individualUserIds, individuals]);
  const individualQuery = useGetIndividualsByUserIdsQuery(
    missingIndividualUserIds.size ? { userIds: missingIndividualUserIds } : skipToken,
  );

  const [selectedLeader, setSelectedLeader] = useState<EntityMember | null>(null);
  const [selectedMembers, setSelectedMembers] = useState<EntityMember[]>([]);
  const [expandedTemplates, setExpandedTemplates] = useState<Set<TeamTemplate>>(new Set());

  const issues = useMemo(
    () =>
      props.issues?.entries.filter((issue) =>
        [
          ProposalField.Team,
          ProposalField.TeamRestriction,
          ProposalField.TeamLeader,
          ProposalField.TeamMember(issue.field.id ?? Guid.EMPTY),
        ].includes(issue.field),
      ),
    [props.issues?.entries],
  );

  const { popDialog } = useDialog();
  const confirm = useConfirmDialog();
  const isLargeDownDisplay = useMediaQuery((theme) => theme.breakpoints.down("lg"));

  const setDefaultProposalTeamLeader = useCallback(async () => {
    if (Masked.isMasked(proposalBuilder.currentSpec.workAgreement?.vendorTeam)) return;
    if (
      !proposalBuilder.currentSpec.workAgreement?.vendorTeam?.leader &&
      session.accountType === AccountType.Vendor &&
      session.user?.id &&
      session.user.legalEntityId &&
      session.user.individualId
    ) {
      const defaultTeam = new VendorTeam({
        leader: new EntityMember({
          legalEntityId: session.user.legalEntityId,
          userId: session.user.id,
        }),
        members: [],
      });
      onProposalBuilderUpdated(proposalBuilder.setVendorTeam(defaultTeam));
    }
  }, [proposalBuilder, session, onProposalBuilderUpdated]);

  useEffect(() => {
    if (teamTemplates === undefined && !isLoadingTeamTemplates && !errorLoadingTeamTemplates) {
      dispatch(
        populateTeamTemplatesByQuery({
          session,
          query: templateQuery,
        }),
      );
    }
  }, [dispatch, errorLoadingTeamTemplates, isLoadingTeamTemplates, session, teamTemplates, templateQuery]);

  useEffect(() => {
    if (Masked.isMasked(proposalBuilder.currentSpec.workAgreement?.vendorTeam)) return;
    setDefaultProposalTeamLeader();
    setSelectedLeader(proposalBuilder.currentSpec.workAgreement?.vendorTeam?.leader ?? null);
    setSelectedMembers(proposalBuilder.currentSpec.workAgreement?.vendorTeam?.members ?? []);
  }, [proposalBuilder, setDefaultProposalTeamLeader]);

  async function updateTeamOnProposalBuilder(leader?: EntityMember, members?: EntityMember[]) {
    if (Masked.isMasked(proposalBuilder.currentSpec.workAgreement?.vendorTeam))
      throw new Error("Cannot modify team when it is masked");
    const team = new VendorTeam({ leader, members: members ?? [] });
    onProposalBuilderUpdated(proposalBuilder.setVendorTeam(team));
  }

  function handleTemplateExpanded(template: TeamTemplate) {
    let updatedExpandedTemplates = new Set(expandedTemplates);
    if (updatedExpandedTemplates.has(template)) {
      updatedExpandedTemplates.delete(template);
    } else {
      updatedExpandedTemplates.add(template);
    }
    setExpandedTemplates(updatedExpandedTemplates);
  }

  async function handleTemplateSelectionToggled(template: TeamTemplate): Promise<void> {
    if (Masked.isMasked(proposalBuilder.currentSpec.workAgreement?.vendorTeam)) {
      throw new Error("Cannot modify team when it is masked");
    }
    try {
      if (!template.id) {
        throw new Error("No template selected");
      }
      let updatedSelectedTemplateIds = session.context?.viewingAsVendor
        ? proposalBuilder.currentSpec.workAgreement?.vendorTeamTemplateIds
        : proposalBuilder.currentSpec.workAgreement?.clientTeamTemplateIds;

      const isAlreadySelected = updatedSelectedTemplateIds?.some((id) => id.isEqualTo(template.id));

      let updatedTeam = proposalBuilder.currentSpec.workAgreement?.vendorTeam;
      if (isAlreadySelected) {
        updatedSelectedTemplateIds = updatedSelectedTemplateIds?.filter((id) => !id.isEqualTo(template.id));
      } else {
        updatedSelectedTemplateIds?.push(template.id);
        const newLeader = await getNewLeaderFromTemplate(template);
        const members = getNewMembersFromTemplate(template);
        setSelectedLeader(newLeader);
        setSelectedMembers(members);
        updatedTeam = new VendorTeam({ leader: newLeader, members });
      }

      let updatedProposalBuilder = session.context?.viewingAsVendor
        ? proposalBuilder.setVendorTeamTemplateIds(updatedSelectedTemplateIds ?? [])
        : proposalBuilder.setClientTeamTemplateIds(updatedSelectedTemplateIds ?? []);

      if (updatedTeam) updatedProposalBuilder = updatedProposalBuilder.setVendorTeam(updatedTeam);

      onProposalBuilderUpdated(updatedProposalBuilder);
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Failed to apply template. Please try again", {
        variant: "error",
      });
    }
  }

  function getNewMembersFromTemplate(template: TeamTemplate): EntityMember[] {
    return _.uniqWith(
      [...template.members, ...selectedMembers],
      (a, b) => (!a.userId && !b.userId) || (a.userId?.isEqualTo(b.userId) ?? false),
    );
  }

  async function getNewLeaderFromTemplate(template: TeamTemplate): Promise<EntityMember> {
    if (!selectedLeader) return template.leader;

    const templateLeaderAlreadySelected = selectedLeader.userId?.isEqualTo(template.leader.userId);
    if (templateLeaderAlreadySelected) {
      return template.leader;
    } else {
      const response = await confirm({
        title: "Replace leader?",
        message: "Do you want to replace the current vendor-team leader with the one from the template?",
        okButtonText: "Replace",
        cancelButtonText: "Keep Current Leader",
      });

      if (response !== ConfirmResponse.Ok) {
        return selectedLeader;
      }
    }
    return template.leader;
  }

  function handleTeamLeaderUpdated(newLeader: EntityMember | null): void {
    if (getIsMemberAlreadySelected(newLeader)) return;
    if (!newLeader) {
      setSelectedLeader(null);
      updateTeamOnProposalBuilder(undefined, selectedMembers);
      return;
    }

    if (!newLeader.legalEntityId) {
      enqueueSnackbar("Selected leader is not associated with an entity", {
        variant: "error",
      });
      return;
    }

    setSelectedLeader(newLeader);
    updateTeamOnProposalBuilder(newLeader, selectedMembers);
  }

  function handleTemplateLeaderToggled(member: EntityMember): void {
    const isAlreadySelected = getIsMemberAlreadySelected(member);

    let teamLeader: EntityMember | null = member;
    if (isAlreadySelected) {
      teamLeader = null;
    } else if (!teamLeader.legalEntityId) {
      enqueueSnackbar("Selected leader is not associated with an entity", {
        variant: "error",
      });
      return;
    }

    setSelectedLeader(teamLeader ?? null);
    updateTeamOnProposalBuilder(teamLeader ?? undefined, selectedMembers);
  }

  function handleTeamMemberAdded(member: EntityMember) {
    const isAlreadySelected = getIsMemberAlreadySelected(member);

    if (isAlreadySelected) {
      handleTeamMemberRemoved(member.userId);
      return;
    }
    let updatedMembers = [...selectedMembers, member];
    setSelectedMembers(updatedMembers);
    updateTeamOnProposalBuilder(selectedLeader ?? undefined, updatedMembers);
  }

  function getIsMemberAlreadySelected(member: EntityMember | null): boolean {
    if (!member) return false;
    return (
      (selectedLeader?.userId?.isEqualTo(member.userId) ?? false) ||
      selectedMembers.some((m) => m.userId?.isEqualTo(member.userId))
    );
  }

  function handleTeamMemberRemoved(memberUserId?: Guid) {
    if (!memberUserId) {
      // Remove all members
      setSelectedMembers([]);
      updateTeamOnProposalBuilder(selectedLeader ?? undefined, []);
      return;
    }

    const updatedMembers = selectedMembers.filter((m) => !m.userId?.isEqualTo(memberUserId));
    setSelectedMembers(updatedMembers);
    updateTeamOnProposalBuilder(selectedLeader ?? undefined, updatedMembers);
  }

  async function handleMarketplaceTeamSelected(team: MarketplaceTeam) {
    if (selectedLeader || selectedMembers.length > 0) {
      const response = await confirm({
        title: "Replace Team?",
        message: "Are you sure you want to replace the current vendor-team?",
        okButtonText: "Replace",
      });

      if (response === ConfirmResponse.Cancel) return;
    }

    if (!team.leader.profile?.firstName || !team.leader.profile?.lastName) {
      throw new Error("Team leader profile is undefined");
    }

    setSelectedLeader(team.leader.toEntityMember());
    setSelectedMembers(team.members.map((m) => m.toEntityMember()));
    updateTeamOnProposalBuilder(
      team.leader.toEntityMember(),
      team.members.map((m) => m.toEntityMember()),
    );
    popDialog();
  }

  function renderTemplates() {
    const selectedTemplateIds = session.context?.viewingAsVendor
      ? proposalBuilder.currentSpec.workAgreement?.vendorTeamTemplateIds
      : proposalBuilder.currentSpec.workAgreement?.clientTeamTemplateIds;
    return (
      <TemplateList dense subheader={<ListSubheader>Templates</ListSubheader>}>
        {teamTemplates?.totalElements === 0 && (
          <>
            {isLoadingTeamTemplates ? (
              <TemplateLoader size={32} thickness={4} disableShrink />
            ) : (
              <ListItem key="noTemplates">
                <ListItemText>No Templates</ListItemText>
              </ListItem>
            )}
          </>
        )}
        {teamTemplates?.data.map((template: TeamTemplate) => {
          const isSelected = selectedTemplateIds?.some((id) => id.isEqualTo(template.id));

          return (
            <div key={template.id?.value}>
              <ListItem
                secondaryAction={
                  <Tooltip title={`${expandedTemplates.has(template) ? "Hide" : "Show"} Members`}>
                    <span>
                      <IconButton edge="end" onClick={() => handleTemplateExpanded(template)} size="medium">
                        {expandedTemplates.has(template) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </span>
                  </Tooltip>
                }
              >
                <ListItemIcon>
                  <Tooltip title={isSelected ? "Deselect Template" : "Select Template"}>
                    <span>
                      <Checkbox
                        edge="start"
                        color="primary"
                        checked={isSelected}
                        indeterminate={
                          !isSelected &&
                          (template.userIsReferenced(selectedLeader?.userId) ||
                            selectedMembers.some((member) => template.userIsReferenced(member.userId)))
                        }
                        tabIndex={-1}
                        disableRipple
                        onChange={() => handleTemplateSelectionToggled(template)}
                      />
                    </span>
                  </Tooltip>
                </ListItemIcon>
                <ListItemText>{template.name.value}</ListItemText>
              </ListItem>
              <Collapse in={expandedTemplates.has(template)} timeout="auto" sx={{ backgroundColor: "#FAFAFA" }}>
                <Divider />
                <List disablePadding dense subheader={<ListSubheader>Leader</ListSubheader>}>
                  {renderTemplateLeader(template)}
                </List>
                <List disablePadding dense subheader={<ListSubheader>Members</ListSubheader>}>
                  {renderTemplateMembers(template)}
                </List>
                <Divider />
              </Collapse>
            </div>
          );
        })}
      </TemplateList>
    );
  }

  function renderTemplateLeader(template: TeamTemplate) {
    if (individualQuery.isLoading) return <Loader size={24} thickness={4} text="Loading leader..." />;
    const leaderIndividual = individuals.find((i) => i.userId.isEqualTo(template.leader.userId));
    if (!leaderIndividual) return null;
    return (
      <ListItem
        key="teamLeader"
        secondaryAction={
          <Checkbox
            edge="end"
            size="small"
            color="primary"
            checked={selectedLeader?.userId?.isEqualTo(template?.leader?.userId) ?? false}
            tabIndex={-1}
            disableRipple
            onClick={() => handleTemplateLeaderToggled(template?.leader)}
          />
        }
      >
        <ListItemText>{leaderIndividual.fullName}</ListItemText>
      </ListItem>
    );
  }

  function isMemberSelected(member: EntityMember): boolean {
    return selectedMembers?.some((m) => m.userId?.isEqualTo(member.userId));
  }

  function handleMemberCheckboxClick(member: EntityMember) {
    if (isMemberSelected(member)) {
      handleTeamMemberRemoved(member.userId);
    } else {
      handleTeamMemberAdded(member);
    }
  }

  function renderTemplateMembers(template: TeamTemplate) {
    if (individualQuery.isLoading) return <Loader size={24} thickness={4} text="Loading members..." />;
    return template?.members?.map((member) => (
      <ListItem
        key={member.userId?.value ?? Guid.generate().value}
        secondaryAction={
          <Checkbox
            edge="end"
            color="primary"
            size="small"
            checked={isMemberSelected(member)}
            tabIndex={-1}
            disableRipple
            onClick={() => handleMemberCheckboxClick(member)}
          />
        }
        onClick={() => handleTeamMemberAdded(member)}
      >
        <ListItemText
          style={{ whiteSpace: "pre-line" }}
          primary={individuals.find((i) => i.userId.isEqualTo(member.userId))?.fullName}
        />
      </ListItem>
    ));
  }

  if (
    Masked.isMasked(proposalBuilder.currentSpec.workAgreement?.vendorTeam) ||
    Masked.isMasked(proposalBuilder.currentSpec.workAgreement?.teamRestricted)
  ) {
    return "You do not have permission to view this team.";
  }

  return (
    <>
      <HeadControls>
        {(issues?.length ?? 0) > 0 && (
          <IssueContainer active={issues?.some((issue) => issue.field.isEqualTo(activeReviewField)) ?? false}>
            {issues?.map((issue) => (
              <Typography
                key={issue.field?.cacheKey}
                color={issue.level === ProposalIssueLevel.Critical ? "error" : "warning"}
                variant="body1"
              >
                {issue.description}
              </Typography>
            ))}
          </IssueContainer>
        )}
        <FormControlLabel
          checked={proposalBuilder.currentSpec.workAgreement?.teamRestricted?.value}
          control={
            <Switch
              disabled={disableEditing}
              onChange={(_event, checked) => {
                onProposalBuilderUpdated(proposalBuilder.setTeamRestricted(new AHBoolean(checked)));
              }}
              color="primary"
            />
          }
          label={
            proposalBuilder.currentSpec.workAgreement?.teamRestricted?.value ? (
              <strong>Team Restricted</strong>
            ) : (
              "Team Not Restricted"
            )
          }
        />
      </HeadControls>
      <ListContainer container direction="row">
        {!disableEditing && (
          <>
            <TemplatePanel hidden={isDrawerOpen}>
              <TemplateContainer>
                {renderTemplates()}
                {isLargeDownDisplay && <Divider />}
              </TemplateContainer>
            </TemplatePanel>
            {!isLargeDownDisplay && (
              <Grid>
                <Divider orientation="vertical" />
              </Grid>
            )}
          </>
        )}
        <Grid size="grow">
          <TeamSelect
            leader={selectedLeader}
            members={selectedMembers}
            commentThreads={commentThreads}
            disableComments={!isSaved}
            commentsToolTip={commentsToolTip}
            disableEditing={disableEditing}
            activeReviewField={activeReviewField}
            onCommentsClicked={onCommentsClicked}
            onLeaderUpdated={handleTeamLeaderUpdated}
            onMemberAdded={handleTeamMemberAdded}
            onMemberRemoved={handleTeamMemberRemoved}
            onMarketplaceTeamSelected={handleMarketplaceTeamSelected}
          />
        </Grid>
      </ListContainer>
    </>
  );
}
